import { useCallback, useState } from 'react';
import { defineMessages } from 'react-intl';
import classNames from 'classnames';
import dynamic from 'next/dynamic';
import CarouselDisplayTrigger from '@alltrails/amplitude/enums/CarouselDisplayTrigger';
import UpsellTrigger from '@alltrails/amplitude/enums/UpsellTrigger';
import logTrailDetailsActionTapped from '@alltrails/amplitude/events/logTrailDetailsActionTapped';
import InfoDividerDot from '@alltrails/shared/components/cards/InfoDividerDot';
import type { TrailId } from '@alltrails/types/TrailId';
import useUser from '@alltrails/context/hooks/useUser';
import Link from '@alltrails/denali/components/Link';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import { useFormatMessage } from '@alltrails/core/hooks/useFormatMessage';
import useReduxListItems from '@alltrails/lists/hooks/useReduxListItems';
import { useTrailCompleted } from '@alltrails/trails';
import { wrapUrlSafe } from '@alltrails/core/utils/wrapUrlSafe';
import styles from './UgcActions.module.scss';

const SignUpModal = dynamic(() => import('@alltrails/user-auth/components/SignUpModal'), { ssr: false });
const VerifiedCompletedDialog = dynamic(() => import('../VerifiedCompletedDialog/VerifiedCompletedDialog'), { ssr: false });

const messages = defineMessages({
  MARK_COMPLETED: { defaultMessage: 'Mark as completed' },
  REMOVE_COMPLETED: { defaultMessage: 'Remove from completed' },
  SUGGEST_EDIT: { defaultMessage: 'Suggest edit' },
  VERIFIED: { defaultMessage: 'Verified completed' }
});

type UgcActionsProps = {
  className?: string;
  slug: string;
  trailId: TrailId;
};

const UgcActions = ({ className, slug, trailId }: UgcActionsProps) => {
  const {
    formattedDefaultMessages: { MARK_COMPLETED, REMOVE_COMPLETED, SUGGEST_EDIT, VERIFIED }
  } = useFormatMessage(messages);
  const languageRegionCode = useLanguageRegionCode();
  const user = useUser();
  const { isVerified } = useReduxListItems();
  const verified = isVerified('trail', trailId);
  const { completed, toggleTrailCompleted } = useTrailCompleted(trailId);
  const [signupModalState, setSignupModalState] = useState<
    { isOpen: false } | { isOpen: true; trigger: CarouselDisplayTrigger; upsellTrigger: UpsellTrigger }
  >({ isOpen: false });
  const [isVerifiedDialogOpen, setIsVerifiedDialogOpen] = useState(false);

  const onMarkCompletedClick = useCallback(() => {
    logTrailDetailsActionTapped({
      trail_id: trailId.toString(),
      trail_page_action: completed ? 'remove from completed' : 'add to completed',
      trail_page_location: 'reviews'
    });

    if (user) {
      toggleTrailCompleted();
    } else {
      setSignupModalState({
        isOpen: true,
        trigger: 'Add Trail to Completed List',
        upsellTrigger: 'Add Trail to Completed List'
      });
    }
  }, [completed, toggleTrailCompleted, trailId, user]);

  const onSuggestEditClick = useCallback(() => {
    if (!user) {
      setSignupModalState({ isOpen: true, trigger: 'Suggest Trail Edit', upsellTrigger: 'Suggest Trail Edit' });
    }
  }, [user]);

  return (
    <div className={classNames(styles.container, className)}>
      <div className={styles.completedContainer}>
        {verified ? (
          <Link
            className={styles.action}
            onClick={() => {
              logTrailDetailsActionTapped({
                trail_id: trailId.toString(),
                trail_page_action: 'add to completed',
                trail_page_location: 'reviews'
              });
              setIsVerifiedDialogOpen(true);
            }}
          >
            {VERIFIED}
          </Link>
        ) : (
          <Link className={styles.action} onClick={onMarkCompletedClick}>
            {completed ? REMOVE_COMPLETED : MARK_COMPLETED}
          </Link>
        )}
        <InfoDividerDot className={styles.dot} />
      </div>
      <Link
        className={styles.action}
        href={user ? `/trail/${slug}/edit?ref=trail-more-edit` : undefined}
        onClick={onSuggestEditClick}
        target="_blank"
        testId="trail-suggest-edit"
      >
        {SUGGEST_EDIT}
      </Link>
      {isVerifiedDialogOpen && <VerifiedCompletedDialog onClose={() => setIsVerifiedDialogOpen(false)} />}
      {signupModalState.isOpen && (
        <SignUpModal
          closeModal={() => setSignupModalState({ isOpen: false })}
          deeplinkData={{ pageId: trailId, pageType: 'trail' }}
          isOpen={signupModalState.isOpen}
          onSuccess={() => {
            setSignupModalState({ isOpen: false });
            if (signupModalState.trigger === 'Add Trail to Completed List') {
              toggleTrailCompleted();
            } else {
              window.open(wrapUrlSafe(`/trail/${slug}/edit?ref=trail-more-edit`, languageRegionCode), '_blank');
            }
          }}
          trigger={signupModalState.trigger}
          upsellTrigger={signupModalState.upsellTrigger}
        />
      )}
    </div>
  );
};

export default UgcActions;
